/**
 * ScheduleCalendar Component
 * Extracted calendar rendering block from Schedule.jsx for maintainability
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  User,
} from 'lucide-react';
import { format, isSameMonth, isToday } from 'date-fns';
import { ShiftCard } from './ShiftCard';
import { MobileScheduleAgenda } from './MobileScheduleAgenda';

// Shift type color definitions
const SHIFT_COLORS = {
  regular: { bg: 'bg-[#1ABC9C]/20', border: 'border-[#1ABC9C]', text: 'text-[#1ABC9C]', dot: 'bg-[#1ABC9C]' },
  night: { bg: 'bg-indigo-500/20', border: 'border-indigo-500', text: 'text-indigo-400', dot: 'bg-indigo-500' },
  standby: { bg: 'bg-[#FFC04D]/20', border: 'border-[#FFC04D]', text: 'text-[#FFC04D]', dot: 'bg-[#FFC04D]' },
  emergency: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500' },
};

/**
 * ScheduleCalendar - Renders the calendar grid with shifts
 * @param {Object} props
 * @param {Array} props.paddedDays - Array of days (with null padding for month view)
 * @param {string} props.viewMode - 'week' or 'month'
 * @param {Date} props.currentDate - Current date being viewed
 * @param {Object} props.draggedShift - Currently dragged shift (or null)
 * @param {Array} props.medics - List of medics
 * @param {string|null} props.expandedShiftId - ID of expanded shift card
 * @param {Function} props.getShiftsForDay - Function to get shifts for a specific day
 * @param {Function} props.getShiftConflicts - Function to check for shift conflicts
 * @param {Function} props.handleDayClick - Handler for clicking a day
 * @param {Function} props.handleDragOver - Handler for drag over event
 * @param {Function} props.handleDrop - Handler for drop event
 * @param {Function} props.handleDragStart - Handler for drag start event
 * @param {Function} props.toggleShiftExpand - Handler for toggling shift expansion
 * @param {Function} props.closeExpandedShift - Handler for closing expanded shift
 * @param {Function} props.handleUpdateShift - Handler for updating a shift
 * @param {Function} props.handleMedicAssignment - Handler for assigning medic to shift
 * @param {Function} props.handleDeleteShift - Handler for deleting a shift
 */
export function ScheduleCalendar({
  paddedDays,
  viewMode,
  currentDate,
  draggedShift,
  medics,
  expandedShiftId,
  getShiftsForDay,
  getLeaveForDay,
  getShiftConflicts,
  handleDayClick,
  handleDragOver,
  handleDrop,
  handleDragStart,
  toggleShiftExpand,
  closeExpandedShift,
  handleUpdateShift,
  handleMedicAssignment,
  handleDeleteShift,
  handleMarkNoShow,
}) {
  return (
    <>
    {/* Mobile agenda (< lg: 1024px) */}
    <div className="block lg:hidden">
      <MobileScheduleAgenda
        paddedDays={paddedDays}
        viewMode={viewMode}
        currentDate={currentDate}
        draggedShift={draggedShift}
        medics={medics}
        expandedShiftId={expandedShiftId}
        getShiftsForDay={getShiftsForDay}
        getLeaveForDay={getLeaveForDay}
        getShiftConflicts={getShiftConflicts}
        handleDayClick={handleDayClick}
        toggleShiftExpand={toggleShiftExpand}
        closeExpandedShift={closeExpandedShift}
        handleUpdateShift={handleUpdateShift}
        handleMedicAssignment={handleMedicAssignment}
        handleDeleteShift={handleDeleteShift}
        handleMarkNoShow={handleMarkNoShow}
      />
    </div>

    {/* Desktop grid (>= lg: 1024px) — iter72.8 fills the viewport so
        the calendar is the dominant element on laptop screens. The
        height calc leaves room for the header + breadcrumb + stats +
        controls + legend above/below. Clamped so very short viewports
        still get a usable minimum. */}
    <Card
      className="hidden lg:flex lg:flex-col bg-[#2B2B2B] border-white/5 overflow-hidden"
      data-testid="schedule-calendar"
      style={{ height: 'clamp(520px, calc(100vh - var(--schedule-calendar-offset, 340px)), 1100px)' }}
    >
      <CardContent className="p-0 flex-1 flex flex-col min-h-0">
        {/* Scrollable calendar container */}
        <div className="overflow-x-auto flex-1 flex flex-col min-h-0">
          <div className="min-w-[700px] flex-1 flex flex-col min-h-0">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 border-b border-white/5 flex-shrink-0">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 md:p-3 text-center text-xs md:text-sm font-medium text-[#A1A1AA] bg-[#1A1A1A]">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days — grow to fill the Card */}
            <div className="grid grid-cols-7 flex-1 min-h-0">
              {paddedDays.map((day, index) => {
                if (!day) {
                  return (
                    <div 
                      key={`pad-${index}`} 
                      className={`${viewMode === 'week' ? 'h-full min-h-[320px]' : 'min-h-[160px]'} bg-[#1A1A1A]/50 border-r border-b border-white/5`} 
                    />
                  );
                }

                const dayShifts = getShiftsForDay(day);
                const dayLeave = getLeaveForDay ? getLeaveForDay(day) : [];
                const isCurrentMonth = viewMode === 'month' ? isSameMonth(day, currentDate) : true;
                const dayIsToday = isToday(day);
                const hasUnassigned = dayShifts.some(s => !s.medic_id);
                const hasConflicts = dayShifts.some(s => getShiftConflicts(s).length > 0);

                return (
                  <div
                    key={day.toISOString()}
                    className={`
                      ${viewMode === 'week' ? 'h-full min-h-[320px]' : 'min-h-[160px]'} 
                      p-2 border-r border-b border-white/5 cursor-pointer
                      transition-colors duration-200
                      flex flex-col min-h-0
                      ${!isCurrentMonth ? 'bg-[#1A1A1A]/50 opacity-50' : 'hover:bg-white/5'}
                      ${dayIsToday ? 'bg-[#1ABC9C]/5' : ''}
                      ${draggedShift ? 'hover:bg-[#1ABC9C]/10' : ''}
                    `}
                    onClick={() => handleDayClick(day)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day)}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    {/* Day Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className={`
                        text-sm 
                        ${dayIsToday ? 'w-7 h-7 rounded-full bg-[#1ABC9C] flex items-center justify-center text-white font-medium' : 'text-[#A1A1AA]'}
                      `}>
                        {format(day, 'd')}
                      </div>
                      <div className="flex items-center gap-1">
                        {dayLeave.length > 0 && (
                          <Badge
                            className="bg-[#FFC04D]/15 text-[#FFC04D] border-0 text-xs"
                            title={`On leave: ${dayLeave.map(l => l.medic_name).join(', ')}`}
                            data-testid={`leave-badge-${format(day, 'yyyy-MM-dd')}`}
                          >
                            {dayLeave.length} on leave
                          </Badge>
                        )}
                        {dayShifts.length > 0 && (
                          <Badge className="bg-white/10 text-[#A1A1AA] border-0 text-xs">
                            {dayShifts.length}
                          </Badge>
                        )}
                        {hasUnassigned && (
                          <div className="w-2 h-2 rounded-full bg-[#FFC04D]" title="Unassigned shifts" />
                        )}
                        {hasConflicts && (
                          <div className="w-2 h-2 rounded-full bg-red-500" title="Conflicts detected" />
                        )}
                      </div>
                    </div>

                    {/* Shifts — week view fills remaining vertical space;
                        month view keeps a fixed slot per day. */}
                    <ScrollArea className={viewMode === 'week' ? 'flex-1 min-h-0' : 'h-[100px]'}>
                      <div className="space-y-2.5 pr-2 pb-1 w-full">
                        {viewMode === 'week' ? (
                          // Week view: Show all shifts with full cards
                          dayShifts.map(shift => (
                            <ShiftCard 
                              key={shift.id} 
                              shift={shift}
                              medics={medics}
                              expandedShiftId={expandedShiftId}
                              draggedShift={draggedShift}
                              getShiftConflicts={getShiftConflicts}
                              toggleShiftExpand={toggleShiftExpand}
                              closeExpandedShift={closeExpandedShift}
                              handleDragStart={handleDragStart}
                              handleUpdateShift={handleUpdateShift}
                              handleMedicAssignment={handleMedicAssignment}
                              handleDeleteShift={handleDeleteShift}
                              handleMarkNoShow={handleMarkNoShow}
                            />
                          ))
                        ) : (
                          // Month view: Compact display
                          <>
                            {dayShifts.slice(0, 3).map(shift => {
                              const colors = SHIFT_COLORS[shift.shift_type] || SHIFT_COLORS.regular;
                              const seg = shift._multiDaySegment;
                              const isMulti = !!seg;
                              return (
                                <div
                                  key={`${shift.id}-${seg || 'single'}`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, shift)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleShiftExpand(shift.id, e);
                                  }}
                                  className={`
                                    text-xs p-1.5 cursor-grab
                                    ${isMulti ? 'bg-indigo-500/25 border-indigo-400/60' : `${colors.bg} ${colors.border}`}
                                    ${isMulti ? 'border-y border-l-0 border-r-0' : 'rounded border-l-2'}
                                    ${seg === 'start' ? 'rounded-l border-l-2 ml-0' : ''}
                                    ${seg === 'end' ? 'rounded-r border-r-2 mr-0' : ''}
                                    ${seg === 'middle' ? 'rounded-none -ml-2 -mr-2' : ''}
                                    hover:opacity-80 transition-all
                                  `}
                                  title={isMulti ? `Multi-day: ${shift.date} → ${shift.end_date} (Day ${shift._dayIndex} of ${shift._dayTotal})` : undefined}
                                  data-testid={`shift-compact-${shift.id}-${seg || 'single'}`}
                                >
                                  <div className="flex items-center gap-1">
                                    <span className={`${isMulti ? 'text-indigo-200' : colors.text} font-medium`}>
                                      {isMulti
                                        ? (seg === 'start' ? `▶ ${shift.start_time}` : seg === 'end' ? `◀ ${shift.end_time}` : '···')
                                        : shift.start_time}
                                    </span>
                                    {shift.medic_name && (
                                      <span className="text-[#F2F2F2] truncate flex-1">
                                        - {shift.medic_name.split(' ')[0]}
                                      </span>
                                    )}
                                    {!shift.medic_id && <User className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" />}
                                  </div>
                                </div>
                              );
                            })}
                            {dayShifts.length > 3 && (
                              <div className="text-xs text-[#A1A1AA] pl-1 font-medium">
                                +{dayShifts.length - 3} more
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
}

/**
 * CalendarLegend - Renders the legend below the calendar
 */
export function CalendarLegend() {
  return (
    <div className="flex flex-wrap gap-4 items-center" data-testid="calendar-legend">
      <span className="text-sm text-[#A1A1AA]">Shift Types:</span>
      {Object.entries(SHIFT_COLORS).map(([type, colors]) => (
        <div key={type} className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
          <span className="text-sm text-[#A1A1AA] capitalize">{type}</span>
        </div>
      ))}
      <span className="text-[#A1A1AA]">|</span>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#FFC04D]" />
        <span className="text-sm text-[#A1A1AA]">Unassigned</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-sm text-[#A1A1AA]">Conflict</span>
      </div>
      <span className="text-[#A1A1AA]">|</span>
      <span className="text-xs text-[#A1A1AA] italic">Drag shifts to move dates</span>
    </div>
  );
}

export { SHIFT_COLORS };
